/** @format */

import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  FaArrowCircleDown,
  FaArrowCircleUp,
  FaPauseCircle,
  FaCheck,
} from "react-icons/fa";
import { FaCircleInfo, FaCopy } from "react-icons/fa6";
import styles from "../../../styles/Components/trading-assistant/tradeAssistantList.module.sass";
import AssetDisplay from "../../../ui/AssetDisplay";
import TradeButton from "../../../shared/trade-form/TradeButton";
import HideContentIcon from "../../mini-app/shared/HideContentIcon";
import AIAssistantServiceActivationModal from "../AIAssistantServiceActivationModal";
import { currencyFormatter } from "../../../utility/helper";
import { Popover } from "antd";
import SignalMoreInfo from "./SignalMoreInfo";
import useSuggestedSignal from "../../../hooks/useSuggestedSignal";
import { Typography } from "antd";
import PositionSizeLeverage from "../../../shared/components/PositionSizeLeverage";

const { Paragraph } = Typography;

const ListItem = ({ data, setShowServiceActivationModal }) => {
  const {
    tradingAssistant: {
      aiMarketType,
    },
    profile: { profileData },
    liveSuggestion: { suggestions },
  } = useSelector((state) => state);

  const isServiceActive = profileData?.myais_active === "True";

  const { SYMBOL_NAME: symbol } = data;
  const liveData = suggestions?.find((s) => s?.symbol === data?.SYMBOL_NAME);
  const prediction =
    aiMarketType === "crypto" &&
    liveData?.prediction &&
    liveData?.prediction !== "Hold"
      ? liveData?.prediction
      : data?.TRADE_TYPE?.split("/")?.[0];

  const renderSignalIcon = (prediction) => {
    if (prediction === "Sell") return <FaArrowCircleDown size={18} />;
    if (prediction === "Buy") return <FaArrowCircleUp size={18} />;
    return <FaPauseCircle size={18} />;
  };

  const { signal, stopLoss, takeProfit, positionSize, signalDate, aiLeverage } =
    useSuggestedSignal({ coin: symbol, oppositeSignal: "ai" });

  return (
    <div className={styles.listItem}>
      <div className={styles.itemHeader}>
        <div className={styles.pairInfo}>
          <AssetDisplay
            asset={data?.SYMBOL_NAME}
            isForex={aiMarketType === "forex"}
          />
        </div>
      </div>

      <div className={styles.orderSection}>
        <div className={styles.placeOrder}>Place Order:</div>
        <TradeButton
          disabled={aiMarketType !== "crypto"}
          btnClassName={`${
            isServiceActive
              ? prediction === "Buy"
                ? styles.buyButton
                : prediction === "Sell"
                  ? styles.sellButton
                  : styles.holdButton
              : styles.hideContent
          }`}
          data={{ coin: data?.SYMBOL_NAME }}
          service="aiAssistant"
          onClick={() => {
            if (aiMarketType !== "crypto") return;
            if (!isServiceActive) {
              return setShowServiceActivationModal(true);
            }
          }}
        >
          {!isServiceActive ? (
            <HideContentIcon style={{ padding: ".5rem" }} />
          ) : (
            <>
              {renderSignalIcon(prediction)}
              {prediction}
            </>
          )}
        </TradeButton>
      </div>

      <div className={styles.signalSection}>
        <div className={`${styles.signal}`}>
          <span className={styles.signalLabel}>Latest Signal:</span>
          {isServiceActive ? (
            <div className={styles.signalInfo}>
              <span
                className={`${styles.signalType} ${
                  styles?.[
                    `${data?.TRADE_TYPE?.split("/")?.[0]?.toLowerCase()}Status`
                  ]
                }`}
              >
                <Popover
                  content={<SignalMoreInfo data={data} />}
                  placement="bottom"
                  className={styles.categoryTooltip}
                >
                  <FaCircleInfo color="#2A86FF" />
                </Popover>
                {signal}
              </span>
              <span className={styles.time}>{signalDate}</span>
            </div>
          ) : (
            <HideContentIcon
              clickHandler={(e) => {
                e.stopPropagation();
                setShowServiceActivationModal(true);
              }}
            />
          )}
        </div>
      </div>

      <div className={styles.infoGrid}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Current Price:</span>
          <span className={styles.infoValue}>
            {isServiceActive ? (
              <span
                style={data?.priceColor ? { color: `${data?.priceColor}` } : {}}
              >
                ${currencyFormatter(data?.CURRENT_PRICE)}
              </span>
            ) : (
              <HideContentIcon />
            )}
          </span>
        </div>

        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Stop Loss:</span>
          <span className={styles.infoValue}>
            {isServiceActive ? (
              <Paragraph
                copyable={{
                  text: `${Number(stopLoss)?.toFixed(2)}`,
                  icon: [
                    <FaCopy color="#0C4CFC" key="copy-icon" />,
                    <FaCheck color="green" key="copied-icon" />,
                  ],
                  tooltips: ["Copy", "Copied!"],
                }}
                className={styles.copyableText}
              >
                {Number(stopLoss)?.toFixed(2)}%
              </Paragraph>
            ) : (
              <HideContentIcon />
            )}
          </span>
        </div>

        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Take Profit:</span>
          <span className={styles.infoValue}>
            {isServiceActive ? (
              <Paragraph
                copyable={{
                  text: `${Number(takeProfit)?.toFixed(2)}`,
                  icon: [
                    <FaCopy color="#0C4CFC" key="copy-icon" />,
                    <FaCheck color="green" key="copied-icon" />,
                  ],
                  tooltips: ["Copy", "Copied!"],
                }}
                className={styles.copyableText}
              >
                {Number(takeProfit)?.toFixed(2)}%
              </Paragraph>
            ) : (
              <HideContentIcon />
            )}
          </span>
        </div>

        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Position Size:</span>
          <span className={styles.infoValue}>
            {isServiceActive ? (
              <Paragraph
                copyable={{
                  text: `${Number(positionSize)?.toFixed(2)}`,
                  icon: [
                    <FaCopy color="#0C4CFC" key="copy-icon" />,
                    <FaCheck color="green" key="copied-icon" />,
                  ],
                  tooltips: ["Copy", "Copied!"],
                }}
                className={styles.copyableText}
              >
                <PositionSizeLeverage
                  stopLoss={Number(Number(stopLoss)?.toFixed(2))}
                  suggestedLeverage={aiLeverage}
                />
                {Number(positionSize)?.toFixed(2)}%
              </Paragraph>
            ) : (
              <HideContentIcon />
            )}
          </span>
        </div>
      </div>
    </div>
  );
};

const TradeAssistantList = ({ data = [] }) => {

  const [showServiceActivationModal, setShowServiceActivationModal] =
    useState(false);

  return (
    <div className={styles.listContainer}>
      {data.map((item, index) => {
        return (
          <ListItem
            data={item}
            key={index}
            setShowServiceActivationModal={setShowServiceActivationModal}
          />
        );
      })}

      <AIAssistantServiceActivationModal
        showActivationModal={showServiceActivationModal}
        setShowActivationModal={setShowServiceActivationModal}
      />
    </div>
  );
};

export default TradeAssistantList;
